import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { 
  ShoppingBag, 
  RefreshCw, 
  ExternalLink, 
  CheckCircle, 
  AlertTriangle,
  ArrowRightLeft,
  Package,
  Settings2
} from 'lucide-react';
import api from '@/lib/api';
import { useToast } from '@/hooks/useToast';
import { motion } from 'framer-motion';

export function Marketplace() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: configs } = useQuery({
    queryKey: ['marketplaces'],
    queryFn: async () => {
      const response = await api.get('/marketplaces/status');
      return response.data;
    },
    retry: false,
    initialData: []
  });

  const syncMutation = useMutation({
    mutationFn: () => api.post('/marketplaces/sync/estoque'),
    onSuccess: () => {
      toast({ title: 'Sucesso', description: 'Sincronização de estoque iniciada!' });
      queryClient.invalidateQueries({ queryKey: ['marketplaces'] });
    }
  });

  const getStatusIcon = (provider: string) => {
    const config = configs?.find((c: any) => c.provider === provider);
    if (!config || !config.active) return <AlertTriangle className="h-5 w-5 text-amber-500" />;
    return <CheckCircle className="h-5 w-5 text-emerald-500" />;
  };

  const mlConfig = configs?.find((c: any) => c.provider === 'MERCADOLIVRE');
  const shopeeConfig = configs?.find((c: any) => c.provider === 'SHOPEE');

  return (
    <div className="space-y-8 w-full p-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white p-8 rounded-3xl border border-gray-100 shadow-sm">
        <div>
          <h1 className="text-3xl font-extrabold text-gray-900 tracking-tight">Marketplaces</h1>
          <p className="text-gray-500 mt-2 text-lg">Gerencie a integração com Mercado Livre e Shopee</p>
        </div>
        <div className="flex gap-3">
          <Button 
            variant="outline" 
            className="rounded-xl px-6"
            onClick={() => queryClient.invalidateQueries({ queryKey: ['marketplaces'] })}
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Atualizar Status
          </Button>
          <Button 
            variant="nuvem" 
            className="rounded-xl px-6 shadow-lg shadow-primary/20"
            onClick={() => syncMutation.mutate()}
            isLoading={syncMutation.isPending}
          >
            <ArrowRightLeft className="h-4 w-4 mr-2" />
            Sincronizar Tudo
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Mercado Livre */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <Card className="overflow-hidden border-none shadow-xl hover:shadow-2xl transition-shadow duration-300 rounded-3xl bg-gradient-to-br from-white to-yellow-50/30">
            <CardHeader className="bg-yellow-400/10 p-8 border-b border-yellow-400/20">
              <div className="flex items-center justify-between mb-2">
                <div className="bg-yellow-400 p-3 rounded-2xl shadow-lg shadow-yellow-400/20">
                  <ShoppingBag className="h-7 w-7 text-yellow-900" />
                </div>
                {getStatusIcon('MERCADOLIVRE')}
              </div>
              <CardTitle className="text-2xl font-bold text-gray-900">Mercado Livre</CardTitle>
              <CardDescription className="text-gray-600 font-medium">Sincronização de estoque e pedidos</CardDescription>
            </CardHeader>
            <CardContent className="p-8 space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white p-4 rounded-2xl border border-gray-100 shadow-sm">
                  <span className="text-xs font-bold text-gray-400 uppercase tracking-widest">Produtos Linkados</span>
                  <p className="text-2xl font-black text-gray-900 mt-1">{mlConfig?.produtosSincronizados || 0}</p>
                </div>
                <div className="bg-white p-4 rounded-2xl border border-gray-100 shadow-sm">
                  <span className="text-xs font-bold text-gray-400 uppercase tracking-widest">Status da API</span>
                  {mlConfig?.active ? (
                    <div className="mt-2 text-sm font-semibold text-emerald-600 flex items-center gap-1">
                      <div className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
                      Operacional
                    </div>
                  ) : (
                    <div className="mt-2 text-sm font-semibold text-amber-600 flex items-center gap-1">
                      <div className="h-2 w-2 rounded-full bg-amber-500" />
                      Desconectado
                    </div>
                  )}
                </div>
              </div>

              <div className="space-y-3">
                <Button variant="outline" className="w-full justify-between group hover:border-yellow-400 rounded-xl py-6" onClick={() => window.open('https://auth.mercadolivre.com.br', '_blank')}>
                  Configurar OAuth2 ML
                  <Settings2 className="h-4 w-4 text-gray-400 group-hover:text-yellow-500 transition-colors" />
                </Button>
                <Button variant="outline" className="w-full justify-between group rounded-xl py-6">
                  Mapear Categorias
                  <Package className="h-4 w-4 text-gray-400 group-hover:text-primary transition-colors" />
                </Button>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Shopee */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
          <Card className="overflow-hidden border-none shadow-xl hover:shadow-2xl transition-shadow duration-300 rounded-3xl bg-gradient-to-br from-white to-orange-50/30">
            <CardHeader className="bg-orange-500/10 p-8 border-b border-orange-500/20">
              <div className="flex items-center justify-between mb-2">
                <div className="bg-orange-600 p-3 rounded-2xl shadow-lg shadow-orange-600/20">
                  <ShoppingBag className="h-7 w-7 text-white" />
                </div>
                {getStatusIcon('SHOPEE')}
              </div>
              <CardTitle className="text-2xl font-bold text-gray-900">Shopee</CardTitle>
              <CardDescription className="text-gray-600 font-medium">Integração oficial via API v2</CardDescription>
            </CardHeader>
            <CardContent className="p-8 space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white p-4 rounded-2xl border border-gray-100 shadow-sm">
                  <span className="text-xs font-bold text-gray-400 uppercase tracking-widest">Produtos Linkados</span>
                  <p className="text-2xl font-black text-gray-900 mt-1">{shopeeConfig?.produtosSincronizados || 0}</p>
                </div>
                <div className="bg-white p-4 rounded-2xl border border-gray-100 shadow-sm">
                  <span className="text-xs font-bold text-gray-400 uppercase tracking-widest">Status da API</span>
                  {shopeeConfig?.active ? (
                    <div className="mt-2 text-sm font-semibold text-emerald-600 flex items-center gap-1">
                      <div className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
                      Operacional
                    </div>
                  ) : (
                    <div className="mt-2 text-sm font-semibold text-amber-600 flex items-center gap-1">
                      <div className="h-2 w-2 rounded-full bg-amber-500" />
                      Desconectado
                    </div>
                  )}
                </div>
              </div>

              <div className="space-y-3">
                <Button variant="outline" className="w-full justify-between group hover:border-orange-500 rounded-xl py-6">
                  Autorizar Loja Shopee
                  <ExternalLink className="h-4 w-4 text-gray-400 group-hover:text-orange-500 transition-colors" />
                </Button>
                <Button variant="outline" className="w-full justify-between group rounded-xl py-6">
                  Configurações Logísticas
                  <Settings2 className="h-4 w-4 text-gray-400 group-hover:text-primary transition-colors" />
                </Button>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Tabela de Logs de Sync */}
      <Card className="rounded-3xl border-none shadow-xl overflow-hidden">
        <CardHeader className="p-8 pb-4">
          <CardTitle className="text-xl font-bold flex items-center gap-2">
            <RefreshCw className="h-5 w-5 text-primary" />
            Últimas Sincronizações
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="bg-gray-50/50">
                  <th className="px-8 py-4 text-xs font-bold text-gray-400 uppercase tracking-widest">ID Produto</th>
                  <th className="px-8 py-4 text-xs font-bold text-gray-400 uppercase tracking-widest">Marketplace</th>
                  <th className="px-8 py-4 text-xs font-bold text-gray-400 uppercase tracking-widest">Tipo</th>
                  <th className="px-8 py-4 text-xs font-bold text-gray-400 uppercase tracking-widest">Status</th>
                  <th className="px-8 py-4 text-xs font-bold text-gray-400 uppercase tracking-widest">Data/Hora</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                <tr>
                  <td colSpan={5} className="px-8 py-8 text-center text-gray-400 text-sm">
                    Nenhuma sincronização recente.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
